import {
	AfterViewInit,
	ElementRef, OnDestroy
} from '@angular/core';
import { Config } from 'app/config.const';
import { IIntervallo } from 'app/model/ambulatorio';
import { IAppuntamento } from 'app/model/appuntamento';
import * as moment from 'moment';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { OAuth2Service } from '../angular-rest';

import { IDeleteRequest } from 'app/model/appuntamenti';
import 'fullcalendar';
import { BusinessHoursInput, EventObjectInput, View } from 'fullcalendar';
import { OptionsInput } from 'fullcalendar/src/types/input-types';
import { map } from 'rxjs/operators';
import { isObject } from 'util';

/**
 * Classe di base per la sincronizzazione del full-calendar.
 * 
 * Si occupa di impaginare e di sincronizzare il componente
 * full-calendar. Inoltre serve da wrapper per il componente
 * full-calendar che è ancora scritto in jQuery.
 *
 * @export
 * @class FullCalendarComponent
 * @implements {OnChanges}
 * @implements {AfterViewInit}
 * @implements {OnDestroy}
 */
export abstract class CalendarBase implements AfterViewInit, OnDestroy {

	/********************************************
	 *           INTERFACCIA ASTRATTA           *
	 ********************************************
	 * 
	 * Questi sono i metodi che serve implementare 
	 * per far funzionare il componente. E' 
	 * sufficiente implementarli per ottenere un 
	 * componente calendar completo.
	 * 
	 */

	protected calendar: JQuery;

	/**
	 * Questo deve semplicemente restituire l'elemento
	 * HTML (HTMLElement) che conterrà il calendar,
	 * tipicamente una div.
	 *
	 * @readonly
	 * @abstract
	 * @type {ElementRef}
	 * @memberof CalendarBase
	 */
	abstract get container(): ElementRef;

	/**
	 * Quando implementato nella classe derivata, deve
	 * restituire un Observable (per gestire i 
	 * cambiamenti, se presenti) di FullCalendar.OptionInput
	 * per inizializzare/aggiornare le sue opzioni.
	 *
	 * @readonly
	 * @protected
	 * @abstract
	 * @type {Observable<OptionsInput>}
	 * @memberof CalendarBase
	 */
	protected abstract get settings(): Observable<OptionsInput>;

	/**
	 * Callback dell'evento click, scatenato
	 * quando viene clickato un evento.
	 *
	 * @protected
	 * @abstract
	 * @param {HTMLElement} element
	 * @param {EventObjectInput} event
	 * @param {MouseEvent} jsEvent
	 * @param {View} view
	 * @returns {(boolean | void)}
	 * @memberof CalendarBase
	 */
	protected abstract onEventClick(element: HTMLElement, event: EventObjectInput, jsEvent: MouseEvent, view: View): boolean | void;

	/**
	 * Callback dell'evento click, scatenato
	 * quando viene clickato un giorno.
	 *
	 * @protected
	 * @abstract
	 * @param {HTMLElement} element
	 * @param {moment.Moment} date
	 * @param {MouseEvent} jsEvent
	 * @param {View} view
	 * @param {*} [resourceObj]
	 * @memberof CalendarBase
	 */
	protected abstract onDayClick(element: HTMLElement, date: moment.Moment, jsEvent: MouseEvent, view: View, resourceObj?: any): void;

	/**
	 * Evento di callback per gestire la pulizia nel
	 * caso di inserimento di plugin/di risorse
	 * all'interno dell'evento. 
	 * 
	 * (Ad es. qtip2, come in FullCalendar.ts)
	 *
	 * @protected
	 * @abstract
	 * @param {EventObjectInput} event
	 * @param {JQuery} element
	 * @param {View} view
	 * @memberof CalendarBase
	 */
	protected abstract onEventDestroy(event: EventObjectInput, element: JQuery, view: View): void;

	/**
	 * Callback di pre-pubblicazione dell'evento.
	 * Può essere usato per gestire effetti/stile
	 * dell'evento in jQuery (Vedere es. qtip2
	 * in FullCalendar.ts).
	 *
	 * @protected
	 * @abstract
	 * @param {EventObjectInput} event
	 * @param {JQuery} element
	 * @param {View} view
	 * @memberof CalendarBase
	 */
	protected abstract onEventRender(event: EventObjectInput, element: JQuery, view: View): void;

	/**
	 * Callback di resize di un evento. Se non serve
	 * un controllo sul drag-drop avanzato, è
	 * sufficiente trattarlo come un callback di 
	 * modifica evento.
	 *
	 * @protected
	 * @abstract
	 * @param {HTMLElement} element
	 * @param {EventObjectInput} event
	 * @param {moment.Duration} delta
	 * @param {Function} revertFunc
	 * @param {Event} jsEvent
	 * @param {*} ui
	 * @param {View} view
	 * @memberof CalendarBase
	 */
	protected abstract onEventResize(element: HTMLElement, event: EventObjectInput, delta: moment.Duration, revertFunc: Function, jsEvent: Event, ui: any, view: View): void;

	/**
	 * Callback di fine drag-drop di un evento. 
	 * Se non serve un controllo sul drag-drop 
	 * avanzato, è sufficiente trattarlo come un 
	 * callback di modifica evento.
	 *
	 * @protected
	 * @abstract
	 * @param {HTMLElement} element
	 * @param {EventObjectInput} event
	 * @param {moment.Duration} delta
	 * @param {Function} revertFunc
	 * @param {Event} jsEvent
	 * @param {*} ui
	 * @param {View} view
	 * @memberof CalendarBase
	 */
	protected abstract onEventDrop(element: HTMLElement, event: EventObjectInput, delta: moment.Duration, revertFunc: Function, jsEvent: Event, ui: any, view: View): void;

	/**
	 * Chiamato quando un'intera vista viene distrutta.
	 * Ad esempio quando si passa dalla vista mensile
	 * a quella settimanale. Può essere usato per liberare
	 * le variabili/sottoscrizioni che non servono più.
	 *
	 * @protected
	 * @abstract
	 * @param {View} view
	 * @param {JQuery} element
	 * @memberof CalendarBase
	 */
	protected abstract onViewDestroy(view: View, element: JQuery): void;

	/**
	 * Questo metodo viene chiamato quando una vista viene
	 * generata, oppure quando si naviga da un intervallo
	 * temporale ad un altro (frecce in alto per il mese).
	 * 
	 * Deve restituire due Observables:
	 * 
	 * - il primo che restituisce, uno alla volta, tutti gli
	 *   appuntamenti e le loro modifiche. Un aggiornamento
	 *   ad un appuntamento non è altro che un oggetto con 
	 *   lo stesso id e verrà aggiornato al posto che aggiunto
	 *   alla grafica.
	 * 
	 * - il secondo che restituisce, uno alla volta, tutti gli
	 *   appuntamenti cancellati tramite il loro id, salvato 
	 *   nella struttura dati IDeleteRquest. 
	 *
	 * Questo permette al componente di riflettere graficamente
	 * i cambiamenti lato server e di gestire la complessità
	 * di comunicazione tra jQuery e Angular.
	 * 
	 * @protected
	 * @abstract
	 * @param {View} view
	 * @param {JQuery} element
	 * @returns {[ Observable<IAppuntamento>, Observable<IDeleteRequest<number>> ]}
	 * @memberof CalendarBase
	 */
	protected abstract onViewRender(view: View, element: JQuery): [ Observable<IAppuntamento>, Observable<IDeleteRequest<number>> ];

	/**
	 * Questo metodo restituisce un Observable di una matrice
	 * di intervalli che rappresentano le ore di apertura del
	 * medico, per permetterne l'impaginazione.
	 *
	 * @protected
	 * @abstract
	 * @param {View} view
	 * @returns {(Observable<IIntervallo[][]>|false)}
	 * @memberof CalendarBase
	 */
	protected abstract getBusinessHours(view: View): Observable<IIntervallo[][]>|false;

	/**
	 * Callback di distruzione del componente.
	 * Da usare per pulizia variabili.
	 * 
	 * @protected
	 * @abstract
	 * @memberof CalendarBase
	 */
	protected abstract onCalendarDestroy();

	/////////////// Implementazione ///////////////

	private calendarSubscription = new Subscription();

	constructor(
		private auther: OAuth2Service
	) {	}

	ngAfterViewInit(): void {
		// Mi prendo un riferimento a fullcalendar
		this.calendar = $(this.container.nativeElement);

		// Faccio subscribe per tutte le cose che mi servono
		this.init();
	}

	protected init() {
		if (this.calendar) {
			this.calendarSubscription.unsubscribe();
			this.calendarSubscription = new Subscription(() => this.onCalendarDestroy());

			// In questo caso, dato che devo estrarre delle informazioni
			// dalla parola chiave "this" lato callback, devo risolvere
			// manualmente il problema e non posso lasciarlo fare a
			// typescript.
			const __this = this;
			
			// Di questa ho bisogno di un riferimento forte
			let intervalSubscription = new Subscription();

			this.calendarSubscription.add(
				this.settings.pipe(
					// Utilizzo map per aggiungere un indice alle
					// impostazioni e distinguere tra inizializzazione
					// e aggiornamento.
					map((settings, i) => [i, settings] as [number, OptionsInput])
				).subscribe(([i, settings]) => {
					if (i === 0) {
						// Inizializzo fullcalendar
						this.calendar.fullCalendar(
							$.extend(
								<OptionsInput>{
									eventClick: function (this: HTMLElement, calEvent, jsEvent, view) {
										__this.onEventClick(this, calEvent, jsEvent, view)
									},
									dayClick: function (this: HTMLElement, date: moment.Moment, jsEvent: MouseEvent, view: View, resourceObj?: any) {
										__this.onDayClick(this, date, jsEvent, view, resourceObj);
									},
									eventDestroy: function (this: HTMLElement, event: EventObjectInput, element: JQuery, view: View) {
										__this.onEventDestroy(event, element, view);	
									},
									eventRender: function (this: HTMLElement, event: EventObjectInput, element: JQuery, view: View) {
										__this.onEventRender(event, element, view);	
									},
									viewRender: function (this: HTMLElement, view: View, element: JQuery) {
										intervalSubscription.unsubscribe();
										__this.calendarSubscription.add(intervalSubscription = new Subscription());
										const [ updates, deletes ] = __this.onViewRender(view, element);

										// Aggiornamenti / inserzioni
										intervalSubscription.add(updates.subscribe({
											error: error => {
												if (isObject(error) && 'errno' in error) {
													switch (error.errno) {
														case 401:
														__this.auther.refresh()
														break;
													}
												}
											},
											next: r => {
												if (__this.calendar) {
													setTimeout(() => {
														// FullCalendar non segue lo schema di immutability
														// anzi, si basa per performance sui riferimenti
														// degli oggetti. Per cui prima devo prendermi il
														// riferimento, aggiornarlo e poi notificare fullcalendar
														// che l'ho modificato
														const appuntamento = __this.calendar
															.fullCalendar('clientEvents', r.id);
					
														if (appuntamento.length) {
															__this.calendar.fullCalendar('updateEvent', $.extend(
																appuntamento[0],
																{ local: false },
																r
															));
														} else {
															// Se non c'erano elementi con quell'id, lo aggiungo
															__this.calendar.fullCalendar('renderEvent', r);
														}
													});
												}
											}
										}));

										intervalSubscription.add(deletes.subscribe(
											d => {
												if (__this.calendar) {
													// FIXME: se l'evento fosse aperto per modifica che si fa?
													//  lo mettiamo almeno un popup che dice che è stato cancellato?
													//  Idem con patate per quanto riguarda il popup...
													__this.calendar.fullCalendar('removeEvents', d.delete);
												}
											})
										);

										const orario = __this.getBusinessHours(view);
										if (orario) {
											intervalSubscription.add(
												orario.subscribe(days => {
													__this.calendar.fullCalendar(
														'option',
														'businessHours',
														days.reduce<BusinessHoursInput[]>((week, day, i) => {
															week.push(...day.map<BusinessHoursInput>(d => ({
																dow: [i + 1],
																start: moment.duration(d.inizio),
																end: moment.duration(d.fine)
															})));
															return week;
														}, [])
													)
												})
											);
										} else {
											__this.calendar.fullCalendar('option', 'businessHours', false);
										}
									},
									viewDestroy: function (view: View, element: JQuery) {
										__this.onViewDestroy(view, element);
									},
									eventResize: function (this: HTMLElement, event: EventObjectInput, delta: moment.Duration, revertFunc: Function, jsEvent: Event, ui: any, view: View) {
										__this.onEventResize(this, event, delta, revertFunc, jsEvent, ui, view);
									},
									eventDrop: function (this: HTMLElement, event: EventObjectInput, delta: moment.Duration, revertFunc: Function, jsEvent: Event, ui: any, view: View) {
										__this.onEventDrop(this, event, delta, revertFunc, jsEvent, ui, view);
									}
								},
								Config.defaultCalendarOptions,
								settings
							));
					} else {
						// Controllo che calendar sia ancora attivo
						// utile durante la distruzione del componente.
						if (this.calendar) {
							this.calendar.fullCalendar('option', settings);
						}
					}
				})
			);

			this.calendarSubscription.add(() => this.calendar.fullCalendar('destroy') );
		}
	}

	ngOnDestroy(): void {
		this.calendarSubscription.unsubscribe();
	}
}



// WEBPACK FOOTER //
// ./src/app/calendar/calendar_base.ts